"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

function createOrbStyle(background, transformOrigin) {
  return {
    width: "var(--size)",
    height: "var(--size)",
    top: "calc(50% - var(--size) / 2)",
    left: "calc(50% - var(--size) / 2)",
    background,
    mixBlendMode: "var(--blending-value)",
    transformOrigin,
  };
}

function createAnimatedOrbStyle(background, transformOrigin, animation) {
  return {
    ...createOrbStyle(background, transformOrigin),
    animation,
  };
}

export function BackgroundGradientAnimation({
  gradientBackgroundStart = "rgb(5, 7, 11)",
  gradientBackgroundEnd = "rgb(24, 18, 11)",
  firstColor = "254, 201, 80",
  secondColor = "255, 176, 72",
  thirdColor = "255, 231, 148",
  fourthColor = "255, 116, 68",
  fifthColor = "184, 132, 48",
  pointerColor = "254, 201, 80",
  size = "84%",
  blendingValue = "hard-light",
  children,
  className,
  interactive = true,
  containerClassName,
  reduceEffects = false,
}) {
  const containerRef = useRef(null);
  const interactiveRef = useRef(null);
  const animationFrameRef = useRef(null);
  const curXRef = useRef(0);
  const curYRef = useRef(0);
  const targetXRef = useRef(0);
  const targetYRef = useRef(0);
  const [isSafari] = useState(
    () =>
      typeof navigator !== "undefined" &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  );

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    node.style.setProperty(
      "--gradient-background-start",
      gradientBackgroundStart,
    );
    node.style.setProperty("--gradient-background-end", gradientBackgroundEnd);
    node.style.setProperty("--first-color", firstColor);
    node.style.setProperty("--second-color", secondColor);
    node.style.setProperty("--third-color", thirdColor);
    node.style.setProperty("--fourth-color", fourthColor);
    node.style.setProperty("--fifth-color", fifthColor);
    node.style.setProperty("--pointer-color", pointerColor);
    node.style.setProperty("--size", size);
    node.style.setProperty("--blending-value", blendingValue);

    return () => {
      node.style.removeProperty("--gradient-background-start");
      node.style.removeProperty("--gradient-background-end");
      node.style.removeProperty("--first-color");
      node.style.removeProperty("--second-color");
      node.style.removeProperty("--third-color");
      node.style.removeProperty("--fourth-color");
      node.style.removeProperty("--fifth-color");
      node.style.removeProperty("--pointer-color");
      node.style.removeProperty("--size");
      node.style.removeProperty("--blending-value");
    };
  }, [
    blendingValue,
    fifthColor,
    firstColor,
    fourthColor,
    gradientBackgroundEnd,
    gradientBackgroundStart,
    pointerColor,
    secondColor,
    size,
    thirdColor,
  ]);

  useEffect(() => {
    if (!interactive) {
      return undefined;
    }

    const animate = () => {
      curXRef.current += (targetXRef.current - curXRef.current) / 20;
      curYRef.current += (targetYRef.current - curYRef.current) / 20;

      if (interactiveRef.current) {
        interactiveRef.current.style.transform = `translate(${Math.round(
          curXRef.current,
        )}px, ${Math.round(curYRef.current)}px)`;
      }

      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [interactive]);

  const handleMouseMove = (event) => {
    if (!interactive || !containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    targetXRef.current = event.clientX - rect.left;
    targetYRef.current = event.clientY - rect.top;
  };

  const gradientsFilterStyle =
    isSafari || reduceEffects
      ? undefined
      : { filter: "url(#aceternity-blur-me) blur(40px)" };
  const orbConfigs = [
    {
      background:
        "radial-gradient(circle at center, rgba(var(--first-color), 0.95) 0, rgba(var(--first-color), 0.6) 30%, rgba(var(--first-color), 0) 58%) no-repeat",
      transformOrigin: "center center",
      animation: "moveVertical 30s ease infinite",
      className: "absolute opacity-100",
    },
    {
      background:
        "radial-gradient(circle at center, rgba(var(--second-color), 0.8) 0, rgba(var(--second-color), 0) 50%) no-repeat",
      transformOrigin: "calc(50% - 400px)",
      animation: "moveInCircle 20s reverse infinite",
      className: "absolute opacity-100",
    },
    {
      background:
        "radial-gradient(circle at center, rgba(var(--third-color), 0.8) 0, rgba(var(--third-color), 0) 50%) no-repeat",
      transformOrigin: "calc(50% + 400px)",
      animation: "moveInCircle 40s linear infinite",
      className: "absolute opacity-100",
    },
    {
      background:
        "radial-gradient(circle at center, rgba(var(--fourth-color), 0.82) 0, rgba(var(--fourth-color), 0) 50%) no-repeat",
      transformOrigin: "calc(50% - 200px)",
      animation: "moveHorizontal 40s ease infinite",
      className: "absolute opacity-80",
    },
    {
      background:
        "radial-gradient(circle at center, rgba(var(--fifth-color), 0.8) 0, rgba(var(--fifth-color), 0) 50%) no-repeat",
      transformOrigin: "calc(50% - 800px) calc(50% + 800px)",
      animation: "moveInCircle 20s ease infinite",
      className: "absolute opacity-100",
    },
  ];
  const visibleOrbConfigs = reduceEffects ? orbConfigs.slice(0, 3) : orbConfigs;

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className={cn(
        "relative left-0 top-0 h-full w-full overflow-hidden isolate bg-[linear-gradient(40deg,var(--gradient-background-start),var(--gradient-background-end))]",
        containerClassName,
      )}
    >
      <svg className="hidden">
        <defs>
          <filter id="aceternity-blur-me">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="10"
              result="blur"
            />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>

      <div className={cn("relative z-10", className)}>{children}</div>

      <div
        aria-hidden="true"
        style={gradientsFilterStyle}
        className={cn(
          "gradients-container pointer-events-none absolute inset-0 h-full w-full",
          reduceEffects ? "blur-md" : "blur-lg",
          isSafari ? "blur-2xl" : undefined,
        )}
      >
        {visibleOrbConfigs.map((orbConfig) => (
          <div
            key={`${orbConfig.transformOrigin}-${orbConfig.animation}`}
            style={createAnimatedOrbStyle(
              orbConfig.background,
              orbConfig.transformOrigin,
              orbConfig.animation,
            )}
            className={orbConfig.className}
          />
        ))}

        {interactive ? (
          <div
            ref={interactiveRef}
            style={{
              background:
                "radial-gradient(circle at center, rgba(var(--pointer-color), 0.8) 0, rgba(var(--pointer-color), 0) 50%) no-repeat",
              mixBlendMode: "var(--blending-value)",
            }}
            className="pointer-events-none absolute -left-1/2 -top-1/2 h-full w-full opacity-70"
          />
        ) : null}
      </div>
    </div>
  );
}
